import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import Layout from './components/Layout'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import './App.css'
import axios from 'axios'
axios.defaults.baseURL = import.meta.env?.VITE_API_BASE ?? '/api'

const Login = lazy(() => import('./pages/Login'))
const Register = lazy(() => import('./pages/Register'))
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'))
const EmailVerificationRequired = lazy(() => import('./pages/EmailVerificationRequired'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const AcceptInvitation = lazy(() => import('./pages/AcceptInvitation'))
const EmployeeInviteSetup = lazy(() => import('./pages/EmployeeInviteSetup'))
const CompleteProfile = lazy(() => import('./pages/CompleteProfile'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Profile = lazy(() => import('./pages/Profile'))
const Claims = lazy(() => import('./pages/Claims'))
const Plans = lazy(() => import('./pages/Plans'))
const PlanSelection = lazy(() => import('./pages/PlanSelection'))
const PaymentStatus = lazy(() => import('./pages/PaymentStatus'))
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'))
const PaymentFailed = lazy(() => import('./pages/PaymentFailed'))
const SecondaryCodes = lazy(() => import('./pages/SecondaryCodes'))
const Hospitals = lazy(() => import('./pages/Hospitals'))
const Subscriptions = lazy(() => import('./pages/Subscriptions'))
const Refer = lazy(() => import('./pages/Refer'))
const History = lazy(() => import('./pages/History'))
const Dependents = lazy(() => import('./pages/Dependents'))
const Settings = lazy(() => import('./pages/Settings'))
const BankDetails = lazy(() => import('./pages/BankDetails'))
const EditBankDetails = lazy(() => import('./pages/EditBankDetails'))
const DigitalID = lazy(() => import('./pages/DigitalID'))
const HelpSupport = lazy(() => import('./pages/HelpSupport'))
const MyOrganization = lazy(() => import('./pages/MyOrganization'))
const Wallet = lazy(() => import('./pages/Wallet'))
const DepositFunds = lazy(() => import('./pages/DepositFunds'))
const Withdrawals = lazy(() => import('./pages/Withdrawals'))
const WalletHistory = lazy(() => import('./pages/WalletHistory'))
const PaymentDetail = lazy(() => import('./pages/PaymentDetail'))
const Transfer = lazy(() => import('./pages/Transfer'))
const TransferCallbackPage = lazy(() => import('./pages/TransferCallbackPage'))
const TransferHistory = lazy(() => import('./pages/TransferHistory'))

function AppRoutes() {
  const { user, loading } = useAuth()
  const routeFallback = (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
    </div>
  )

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!user) {
    return (
      <Suspense fallback={routeFallback}>
      <Routes>
        <Route path="/payments/success" element={<PaymentSuccess />} />
        <Route path="/payments/failed" element={<PaymentFailed />} />
        <Route path="/transfer/callback" element={<TransferCallbackPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/verify-email" element={<VerifyEmail />} />
        <Route path="/email-verification-required" element={<EmailVerificationRequired />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/employee-invite/accept" element={<AcceptInvitation />} />
        <Route path="/employee-invite/setup" element={<EmployeeInviteSetup />} />
        <Route path="/auth/complete-profile" element={<CompleteProfile />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      </Suspense>
    )
  }

  return (
    <Suspense fallback={routeFallback}>
    <Routes>
      <Route path="/payments/success" element={<PaymentSuccess />} />
      <Route path="/payments/failed" element={<PaymentFailed />} />
      <Route path="/transfer/callback" element={<TransferCallbackPage />} />
      <Route path="/" element={<Layout />}>
        <Route index element={<Dashboard />} />
        <Route path="profile" element={<Profile />} />
        <Route path="claims" element={<Claims />} />
        <Route path="plans" element={<Plans />} />
        <Route path="plans/select" element={<PlanSelection />} />
        <Route path="payment/status" element={<PaymentStatus />} />
        <Route path="hospitals" element={<Hospitals />} />
        <Route path="subscriptions" element={<Subscriptions />} />
        <Route path="secondary-codes" element={<SecondaryCodes />} />
        <Route path="dependents" element={<Dependents />} />
        <Route path="history" element={<History />} />
        <Route path="history/payments/:reference" element={<PaymentDetail />} />
        <Route path="refer" element={<Refer />} />
        <Route path="wallet" element={<Wallet />} />
        <Route path="wallet/deposit" element={<DepositFunds />} />
        <Route path="wallet/withdraw" element={<Withdrawals />} />
        <Route path="wallet/history" element={<WalletHistory />} />
        <Route path="transfer" element={<Transfer />} />
        <Route path="transfer/history" element={<TransferHistory />} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/bank-details" element={<BankDetails />} />
        <Route path="settings/bank-details/edit" element={<EditBankDetails />} />
        <Route path="settings/digital-id" element={<DigitalID />} />
        <Route path="settings/help-support" element={<HelpSupport />} />
        <Route path="settings/my-organization" element={<MyOrganization />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  )
}

export default App 